import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Menu, Home, Map, Heart, Star, MessageCircle, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const navigation = [{
  name: "Accueil",
  href: "/",
  icon: Home,
  description: "Découvrez Roca Rio"
}, {
  name: "Mes excursions",
  href: "/excursions",
  icon: Map,
  description: "11 aventures à Rio"
}, {
  name: "À propos",
  href: "/a-propos",
  icon: Heart,
  description: "Notre mission solidaire"
}];

interface HeaderProps {
  hideOnHero?: boolean;
}

export const Header = ({ hideOnHero = false }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visible, setVisible] = useState(!hideOnHero);
  const lastScrollY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentY = window.scrollY;
          
          if (hideOnHero) {
            // On hero pages: show after scrolling past 80% of viewport OR scrolling up
            const pastHero = currentY > window.innerHeight * 0.8;
            const scrollingUp = currentY < lastScrollY.current - 5;
            setVisible(pastHero || (scrollingUp && currentY > 100));
          } else {
            // On other pages: hide on scroll down, show on scroll up
            const scrollingDown = currentY > lastScrollY.current + 5;
            const scrollingUp = currentY < lastScrollY.current - 5;
            
            if (scrollingDown && currentY > 80) {
              setVisible(false);
            } else if (scrollingUp || currentY < 80) {
              setVisible(true);
            }
          }
          
          lastScrollY.current = currentY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hideOnHero]);

  return (
    <>
      {!hideOnHero && <div className="h-14" />}
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          visible
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0 pointer-events-none",
          "bg-background/80 backdrop-blur-lg border-b border-border/30"
        )}
        role="banner"
      >
        <nav className="relative mx-auto flex max-w-7xl items-center justify-between py-1.5 px-4 lg:px-8" aria-label="Navigation principale">
          {/* Logo — compact */}
          <div className="flex lg:flex-1">
            <Link to="/" className="flex items-center transition-all duration-300 focus-visible-ring rounded-xl p-0.5" aria-label="Retour à l'accueil - Roca Rio">
              <img src="/roca-rio-logo.png" alt="Roca Rio" className="h-10 w-auto" />
            </Link>
          </div>
          
          {/* Mobile menu */}
          <div className="flex xl:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="inline-flex items-center justify-center rounded-md p-2 min-h-[40px] min-w-[40px] focus-visible-ring" aria-label="Ouvrir le menu de navigation" aria-expanded={mobileMenuOpen}>
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-96 bg-background/95 backdrop-blur-xl border-l border-border/20" id="mobile-menu">
                <div className="flex flex-col h-full">
                  <div className="flex-shrink-0 mb-8">
                    <Link to="/" className="flex items-center focus-visible-ring rounded-2xl p-2" onClick={() => setMobileMenuOpen(false)}>
                      <img src="/roca-rio-logo.png" alt="Roca Rio" className="h-14 w-auto" />
                    </Link>
                  </div>
                  
                  <nav className="flex-1" aria-label="Navigation mobile">
                    <div className="space-y-2" role="list">
                      {navigation.map((item, index) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.href;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={cn(
                              "block rounded-xl px-4 py-3 min-h-[44px] transition-all group focus-visible-ring",
                              "animate-fade-in",
                              isActive
                                ? "text-primary bg-primary/10"
                                : "text-foreground hover:bg-primary/5 hover:text-primary"
                            )}
                            style={{ animationDelay: `${index * 80}ms` }}
                            role="listitem"
                            aria-current={isActive ? 'page' : undefined}
                          >
                            <div className="flex items-center gap-3">
                              <Icon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                              <span className="font-semibold text-sm">{item.name}</span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </nav>
                  
                  <div className="flex-shrink-0 mt-6 space-y-4 animate-fade-in" style={{ animationDelay: '300ms' }}>
                    <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                      <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                        <Star className="h-4 w-4 text-secondary fill-secondary" />
                        Prêt à explorer Rio ?
                      </h3>
                      <div className="space-y-1.5 mb-4">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                          <span>Excursions authentiques</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                          <span>5/5 ⭐ • Guide francophone</span>
                        </div>
                      </div>
                      <Button asChild className="w-full bg-primary hover:bg-secondary text-primary-foreground min-h-[44px] focus-visible-ring rounded-lg text-sm">
                        <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                          <MessageCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                          C'est parti !
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
          
          {/* Desktop nav — refined */}
          <div className="hidden xl:flex xl:items-center xl:gap-x-1 xl:ml-auto xl:mr-6" role="navigation" aria-label="Navigation desktop">
            {navigation.map(item => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "relative text-sm font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 focus-visible-ring",
                    isActive
                      ? "text-primary"
                      : "text-foreground/70 hover:text-foreground"
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.name}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>
          
          <div className="hidden xl:flex xl:items-center">
            <Button asChild size="sm" className="bg-primary hover:bg-secondary text-primary-foreground transition-all duration-200 h-8 px-4 text-xs font-semibold focus-visible-ring rounded-full uppercase tracking-wide">
              <Link to="/contact">
                C'est parti !
              </Link>
            </Button>
          </div>
        </nav>
      </header>
    </>
  );
};
